import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { Session, User } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from './supabaseClient';

interface AuthContextType {
  session: Session | null;
  user: User | null;
  isAdmin: boolean;
  isLoading: boolean;
  isAdminChecking: boolean;
  isTeamMember: boolean;
  isDeactivatedMember: boolean;
  teamMemberRole: string | null;
  teamMemberId: string | null;
  isTeamMemberChecking: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminChecking, setIsAdminChecking] = useState(true);
  const [isTeamMember, setIsTeamMember] = useState(false);
  const [teamMemberRole, setTeamMemberRole] = useState<string | null>(null);
  const [teamMemberId, setTeamMemberId] = useState<string | null>(null);
  const [isDeactivatedMember, setIsDeactivatedMember] = useState(false);
  const [isTeamMemberChecking, setIsTeamMemberChecking] = useState(true);

  // Ref to track current user ID for use in callbacks (avoids stale closure)
  const currentUserIdRef = useRef<string | null>(null);

  // Keep ref in sync with user state
  useEffect(() => {
    currentUserIdRef.current = user?.id ?? null;
  }, [user]);

  const checkTeamMemberStatus = async (email: string): Promise<{ isMember: boolean; memberId: string | null; role: string | null; deactivated: boolean }> => {
    try {
      const result = await Promise.race([
        supabase.rpc('check_is_team_member', { p_email: email }),
        new Promise<{ data: null; error: { message: string } }>((resolve) =>
          setTimeout(() => resolve({ data: null, error: { message: 'Team member check timed out' } }), 5000)
        ),
      ]);
      if (result.error) {
        return { isMember: false, memberId: null, role: null, deactivated: false };
      }
      const data = result.data as { is_member: boolean; member_id?: string; role?: string; deactivated?: boolean } | null;
      if (data?.is_member) {
        return { isMember: true, memberId: data.member_id || null, role: data.role || null, deactivated: false };
      }
      return { isMember: false, memberId: null, role: null, deactivated: data?.deactivated || false };
    } catch {
      return { isMember: false, memberId: null, role: null, deactivated: false };
    }
  };

  const checkAdminStatus = async (email: string): Promise<boolean> => {
    try {
      const result = await Promise.race([
        supabase.rpc('check_is_admin', { p_email: email }),
        new Promise<{ data: null; error: { message: string } }>((resolve) =>
          setTimeout(() => resolve({ data: null, error: { message: 'Admin check timed out' } }), 5000)
        ),
      ]);
      if (result.error) {
        if (result.error.message !== 'Admin check timed out') {
          console.error('Admin check error:', result.error);
        }
        return false;
      }
      return result.data === true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const loadingCompleted = { current: false };
    // Track whether initAuth found a session — if not, onAuthStateChange will handle it
    const initAuthResolved = { current: false };

    // Safety timeout to prevent infinite loading (clears ALL flags as last resort)
    const safetyTimeout = setTimeout(() => {
      if (isMounted && !loadingCompleted.current) {
        console.warn('Auth initialization timed out, forcing all flags to false');
        loadingCompleted.current = true;
        setIsLoading(false);
        setIsAdminChecking(false);
        setIsTeamMemberChecking(false);
      }
    }, 10000);

    // Run admin + team member checks for a given session
    const runRoleChecks = async (sess: Session) => {
      if (!isMounted) return;
      setIsAdminChecking(true);
      setIsTeamMemberChecking(true);
      const adminStatus = await checkAdminStatus(sess.user.email || '');
      if (!isMounted) return;
      setIsAdmin(adminStatus);
      setIsAdminChecking(false);

      if (!adminStatus) {
        const tmStatus = await checkTeamMemberStatus(sess.user.email || '');
        if (!isMounted) return;
        setIsTeamMember(tmStatus.isMember);
        setIsDeactivatedMember(tmStatus.deactivated);
        setTeamMemberId(tmStatus.memberId);
        setTeamMemberRole(tmStatus.role);

        // Set Sentry user context (team member)
        Sentry.setUser({
          id: sess.user.id,
          email: sess.user.email,
          role: tmStatus.isMember ? `editor:${tmStatus.role}` : 'creator',
        });
      } else {
        // Set Sentry user context (admin)
        Sentry.setUser({ id: sess.user.id, email: sess.user.email, role: 'admin' });
      }
      if (isMounted) setIsTeamMemberChecking(false);
    };

    // Initial session check
    const initAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!isMounted) return;

        if (session) {
          initAuthResolved.current = true;
          setSession(session);
          setUser(session.user);

          // Release main loading gate immediately
          loadingCompleted.current = true;
          setIsLoading(false);
          clearTimeout(safetyTimeout);

          // Role checks run after page is unblocked
          await runRoleChecks(session);
        }
        // If no session: do NOT clear checking flags here.
        // onAuthStateChange may deliver the session momentarily (slow connections).
        // The safety timeout (10s) is the backstop.
      } catch (error) {
        console.error('Auth initialization error:', error);
        if (isMounted) {
          loadingCompleted.current = true;
          setIsLoading(false);
          setIsAdminChecking(false);
          setIsTeamMemberChecking(false);
          clearTimeout(safetyTimeout);
        }
      }
    };

    initAuth();

    // Auth state listener — handles INITIAL_SESSION (slow restore) and SIGNED_IN
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!isMounted) return;

      if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN') && session) {
        const isSameUser = session.user.id === currentUserIdRef.current;
        setSession(session);
        setUser(session.user);

        // Release loading gate if initAuth didn't
        if (!loadingCompleted.current) {
          loadingCompleted.current = true;
          setIsLoading(false);
          clearTimeout(safetyTimeout);
        }

        // Skip re-checking roles if initAuth already handled this user
        if (isSameUser && initAuthResolved.current) return;

        initAuthResolved.current = true;
        await runRoleChecks(session);
      } else if (event === 'INITIAL_SESSION' && !session) {
        // Definitively no session — safe to clear everything
        if (isMounted) {
          loadingCompleted.current = true;
          setIsLoading(false);
          setIsAdminChecking(false);
          setIsTeamMemberChecking(false);
          clearTimeout(safetyTimeout);
        }
      } else if (event === 'SIGNED_OUT') {
        setSession(null);
        setUser(null);
        setIsAdmin(false);
        setIsTeamMember(false);
        setIsDeactivatedMember(false);
        setTeamMemberRole(null);
        setTeamMemberId(null);
        Sentry.setUser(null);
      } else if (event === 'TOKEN_REFRESHED' && session) {
        setSession(session);
        setUser(session.user);
      }
    });

    // Refresh session when page becomes visible again (after minimizing or tab switch)
    // This only updates session/user state - it does NOT re-run admin check or clear admin checking state
    // The onAuthStateChange listener handles TOKEN_REFRESHED events if the token was refreshed
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        supabase.auth.getSession().then(({ data: { session } }) => {
          if (!isMounted) return;
          if (session) {
            setSession(session);
            setUser(session.user);
            // Don't touch isAdmin or isAdminChecking - preserve current admin state
          } else {
            // Only clear everything if there's no session (user was logged out)
            setSession(null);
            setUser(null);
            setIsAdmin(false);
            setIsLoading(false);
            setIsAdminChecking(false);
          }
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ session, user, isAdmin, isLoading, isAdminChecking, isTeamMember, isDeactivatedMember, teamMemberRole, teamMemberId, isTeamMemberChecking, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
